// Client-side <title> and <meta> injection — no new dependency required.
// Sets title, description, canonical, og:*, twitter:* — cleans up on unmount.
import { useEffect } from "react";

function upsertMeta(attr, key, value) {
    if (!value) return null;
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", value);
    return el;
}

function upsertLink(rel, href) {
    if (!href) return null;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
    return el;
}

/**
 * useDocumentMeta({ title, description, image, url, type })
 * type defaults to 'website'. Use 'product' or 'article' when relevant.
 */
export function useDocumentMeta({ title, description, image, url, type = "website" } = {}) {
    useEffect(() => {
        const prevTitle = document.title;
        if (title) document.title = title;

        const created = [];
        const set = (attr, key, val) => { const el = upsertMeta(attr, key, val); if (el) created.push({ el, attr, key }); };

        set("name", "description", description);
        set("property", "og:title", title);
        set("property", "og:description", description);
        set("property", "og:image", image);
        set("property", "og:url", url);
        set("property", "og:type", type);
        set("property", "og:site_name", "Urban Off-Road");
        set("name", "twitter:card", image ? "summary_large_image" : "summary");
        set("name", "twitter:title", title);
        set("name", "twitter:description", description);
        set("name", "twitter:image", image);

        const canonical = upsertLink("canonical", url);

        return () => {
            document.title = prevTitle;
            // We keep meta elements (browsers safe) but wipe out the values we set so stale content
            // doesn't leak into the next page before its own effect runs. Alternatively, remove entirely:
            created.forEach(({ el }) => { el.setAttribute("content", ""); });
            if (canonical) canonical.setAttribute("href", "");
        };
    }, [title, description, image, url, type]);
}
